import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormContext } from './Form';
import { shallowCompare } from '../utility/functions';
import Heading from './Heading';
import { loadingSelectState } from '../GetState';
import HamQuest from '../api/HamQuestGiver';
import Modal from '../Modal';
import TooltipHelper from './TooltipHelper';
import Icon from './Icon';
import ModalFactory from '../Modal';

const Wrapper = styled.div`
    display: ${({ showIf }) => showIf ? 'flex' : 'none'};

    flex-direction: column;
    position: relative;
    flex: ${({ flex }) => flex};
`
const SelectCSS = styled.select`
    display: flex;
    align-items: center;
    gap: 0.2em;
    border: 1px solid ${({ value, theme }) => value ? theme.gray : `${theme.gray}80`};
    border-radius: 0.4rem;
    user-select: none;
    color: ${({ theme }) => theme.text};
    transition: background-color, transform, 0.2s;
    background-color: ${({ theme }) => theme.input.background};
    padding: 0.3em 0.5em;
    font-size: inherit;
    overscroll-behavior: unset;
    min-width: 3em;
    cursor: pointer;
    
    appearance: none;
    
    &::-ms-expand {
        display: none;
    }

    &:disabled {
        color: ${({ theme }) => theme.input.disabledText};
        background-color: ${({ theme }) => theme.input.disabled};
        transform: unset;
        box-shadow: unset;
        cursor: default;
        opacity: 1;
    }

    &:focus {
        outline: none !important;
        border: 1px solid ${({ theme }) => theme.text};
    }
`;

const Option = styled.option`
    color: ${({ theme, disabled }) => disabled ? theme.input.text + '40' : theme.input.text};
    background-color: ${({ theme, disabled }) => disabled ? theme.gray + '40' : theme.input.background};
    padding: 2em;
`
const OptionalFlag = styled.i`
    color: ${({ theme }) => theme.gray};
    font-weight: 500;
    font-size: 0.8em;
`;

const Chevron = styled(Icon)`
    position: absolute;
    bottom: 0.4em;
    right: 0.3em;
    color: ${({ disabled, theme }) => disabled ? theme.input.disabledText : theme.text};
    opacity: ${({ disabled }) => disabled ? 0.6 : 1};
    transform: rotate(${({ focus }) => focus ? 0 : -90}deg);
    transition: all 0.2s;
    cursor: pointer;
    pointer-events: none;
`;

/**
 * `Select` Component for forms.
 * 
 * @param {Object} props - The props for the Select component.
 * @param {Object} props.context - The form context containing state and functions.
 * @param {string} props.name - The name of the select field, used for form submission and as a key in the state.
 * @param {boolean} [props.required=false] - If true, the field is marked as required.
 * @param {boolean} [props.nullable=false] - If true, allows a null option in the select dropdown.
 * @param {boolean} [props.standalone=false] - If true, renders the select field as a standalone component.
 * @param {string} [props.label] - The label text for the select field.
 * @param {boolean} [props.disable=false] - If true, disables the select field.
 * @param {boolean} [props.showIf=true] - If true, render select field.
 * @param {any} [props.value] - The default value of the select field.
 * @param {Function} [props.getOptions] - Function to get dynamic options for the select field.
 * @param {Function} [props.buildOptions] - Function to build options from a given state.
 * @param {React.ReactNode} [props.children] - Children elements, typically used for static options.
 * @param {Array} [props.options] - An array of options for the select field.
 * @returns {React.ReactNode} - The rendered Select component.
 */
const Select = ({ context, ...props }) => {
    const selectRef = useRef();

    const [options, setOptions] = useState(props.options || loadingSelectState);
    const [isFocused, setFocus] = useState(false);

    useEffect(() => {
        if (props.getOptions && props.standalone) {
            HamQuest.get(props.getOptions)
                .then(res => {
                    if (props.buildOptions) {
                        res instanceof Array ?
                            res.forEach(e => props.buildOptions(e))
                            : props.buildOptions(res)
                    }
                    setOptions(res)
                })
                .catch(ex => ModalFactory.catch(ex))
        }
    }, [])

    useEffect(() => {
        if ((props.options) && !shallowCompare((props.options), options)) {
            setOptions(props.options ?? props.children)
        }
    }, [props.options])

    const handleChange = (e) => {
        e = e.target.value;
        if (props.callback)
            props.callback(e);

        context.setErrors(e => {
            return {
                ...e,
                [props.name]: props.required && !e ? true : false
            }
        })
        context.setValues(v => {
            return {
                ...v,
                [props.name]: e
            }
        })
    }

    if (props.nullable) props.nullable = 1;
    if (props.standalone) {
        props.standalone = 1
    }

    useEffect(() => {
        if (context && selectRef) {
            if (!context.values[props.name]) {
                context.setValues(v => {
                    return {
                        ...v,
                        [props.name]: !props.nullable ? selectRef.current.value : ''
                    }
                })
            }
        }
    }, [options])


    return props.standalone ? (
        <Wrapper flex={props.flex || 1} showIf={props.showIf ?? true}>
            <Heading fontSize={0.8}>
                {props.label}
                {context && <TooltipHelper formName={context.formName} name={props.name} />}
                {context && !props.required && !(context.submitting || context.editing || props.disable) && <OptionalFlag>- Optional</OptionalFlag>}
            </Heading>
            <SelectCSS
                onChange={context && handleChange}
                value={context ? context.values[props.name] : props.value || ''}
                disabled={context ? context.submitting || context.editing || props.disable : props.disable}
                ref={selectRef}

                onClick={() => setFocus(!isFocused)}
                onBlur={() => setFocus(false)}
                {...props}
            >
                {props.nullable && <Option key={`nullable-${props.name}`} value={''}></Option>}
                {props.children || (options).filter(Boolean).map((e, i) => <Option key={`SelectKey-${i}`} value={e.value} {...e}>{e.label}</Option>)}
            </SelectCSS>

            <Chevron
                icon='chevronDown'
                focus={isFocused ? 1 : 0}
                disabled={context ? context.submitting || context.editing || props.disable : props.disable}
            />
        </Wrapper>
    ) : (
        <SelectContext
            standalone={1}
            {...props}
        />
    );
}

export default Select;

const SelectContext = ({ ...props }) => {
    const context = useContext(FormContext);
    return (
        <Select
            context={{ ...context }}
            {...props}
        />
    )
}
